import React, { useEffect, useState } from 'react';
import { shortWon, Vehicle, won } from '../types/vehicle';
import { fundInfo } from '../lib/fund';
import { Employee, employeeApi, nameOf } from '../services/employeeApi';

/**
 * 재원 기본정보 · 출자금 사용 현황 — factsheet 웨비나 「펀드 결산」 편 재원 상세 대조 (2026-10-09).
 *
 * 대표 펀드매니저 · 핵심 운용인력 · 보수 체계는 규약 값이다(자금원 행). 확인 전이면 비운다.
 * 출자금 사용은 원장에서 — 납입받은 돈이 투자 · 보수 · 비용 · 분배로 얼마 나갔고 얼마 남았나.
 */
export const FundBasics: React.FC<{
  vehicle: Vehicle; contributed: number; invested: number; fee: number; expense: number; distributed: number; balance: number;
}> = ({ vehicle, contributed, invested, fee, expense, distributed, balance }) => {
  const info = fundInfo(vehicle);
  const [emps, setEmps] = useState<Employee[]>([]);
  useEffect(() => { employeeApi.list().then(setEmps).catch(() => setEmps([])); }, []);
  const lead = nameOf(emps, vehicle.leadManagerEmployeeId) ?? '미정';
  const keys = (vehicle.keyPersonEmployeeIds ?? []).map((id) => nameOf(emps, id)).join(', ') || '미정';
  const parts = [
    { k: '투자', v: invested, c: 'bg-indigo-500' }, { k: '보수', v: fee, c: 'bg-amber-500' },
    { k: '비용', v: expense, c: 'bg-slate-400' }, { k: '분배', v: distributed, c: 'bg-violet-500' },
  ];
  return (
    <div data-tour="basics" className="grid gap-4 lg:grid-cols-2 mb-5">
      <section className="bg-white border border-slate-200 rounded-lg px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-900 mb-2">재원 기본정보</h2>
        {/* 담당자는 규약과 별개 — 규약 값이 없어도 보인다(grkcon-corp/GRK_investment#24) */}
        <dl className="grid grid-cols-[110px_1fr] gap-y-1 text-sm">
          <dt className="text-slate-500">대표 펀드매니저</dt><dd>{lead}</dd>
          <dt className="text-slate-500">핵심 운용인력</dt><dd>{keys}</dd>
        </dl>
        {!info ? <p className="text-sm text-slate-400 pt-2">보수 · 결산 — 규약 확인 전</p> : (
          <dl className="grid grid-cols-[110px_1fr] gap-y-1 text-sm mt-1">
            <dt className="text-slate-500">투자기구 형태</dt><dd>{info.vehicleType ?? '—'} · {info.paymentMethod}</dd>
            <dt className="text-slate-500">보수 체계</dt><dd>관리 {info.mgmtFeeText} · 성과 {(info.carryRate * 100).toFixed(0)}% (기준수익률 {(info.hurdle * 100).toFixed(0)}%)</dd>
            <dt className="text-slate-500">등록일</dt><dd>{vehicle.registeredOn?.replace(/-/g, '.') ?? '미입력'}</dd>
            <dt className="text-slate-500">결산 · 감사</dt><dd>매년 {info.fiscalYearEnd?.replace('-', '.') ?? '—'} · {info.auditor ?? '미정'}</dd>
          </dl>
        )}
      </section>
      <section className="bg-white border border-slate-200 rounded-lg px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-900 mb-2">출자금 사용 현황</h2>
        {contributed === 0 ? <p className="text-sm text-slate-400 py-3">출자 기록 없음</p> : (
          <>
            <div className="flex h-3 rounded-full overflow-hidden bg-slate-100">
              {parts.filter((p) => p.v > 0).map((p) => <div key={p.k} className={p.c} style={{ width: `${Math.min(100, (p.v / contributed) * 100)}%` }} title={`${p.k} ${shortWon(p.v)}`} />)}
            </div>
            <div className="mt-2 grid grid-cols-3 gap-x-3 gap-y-1 text-xs">
              {parts.map((p) => <span key={p.k} className="flex items-center gap-1"><i className={`inline-block w-2 h-2 rounded-sm ${p.c}`} />{p.k} <b className="tabular-nums font-medium">{shortWon(p.v)}</b></span>)}
              <span className="flex items-center gap-1">납입 <b className="tabular-nums font-medium">{shortWon(contributed)}</b></span>
              <span className="flex items-center gap-1">잔액 <b className="tabular-nums font-medium">{won(balance)}</b></span>
            </div>
            {balance > contributed && <p className="mt-1 text-xs text-slate-400">잔액에 회수금이 포함되어 있습니다.</p>}
          </>
        )}
      </section>
    </div>
  );
};
